/**
 * Breakpoint values for responsive design
 */
const breakpoints = {
  xs: '400px',
  sm: '480px',
  smMd: '600px',
  md: '768px',
  lg: '1024px',
  xl: '1200px',
  '2xl': '1280px',
  '3xl': '1440px',
  // Component-specific widths
  formMaxWidth: '640px',
  modalWidth: '520px',
  sidebarWidth: '400px',
} as const;

/**
 * Media query helpers for use in styled-components
 * Mobile-first: styles inside these blocks apply at the breakpoint and UP
 */
export const media = {
  xs: `@media (min-width: ${breakpoints.xs})`,
  sm: `@media (min-width: ${breakpoints.sm})`,
  smMd: `@media (min-width: ${breakpoints.smMd})`,
  md: `@media (min-width: ${breakpoints.md})`,
  lg: `@media (min-width: ${breakpoints.lg})`,
  xl: `@media (min-width: ${breakpoints.xl})`,
  '2xl': `@media (min-width: ${breakpoints['2xl']})`,
  '3xl': `@media (min-width: ${breakpoints['3xl']})`,
} as const;

/**
 * Screen heights the landing sections size themselves to. A section fills
 * one screen, so what matters is how tall that screen is for its
 * orientation, not only how wide.
 */
const heights = {
  /** A landscape screen with little height: a laptop, a tablet on its side. */
  shortLandscape: '950px',
  /** A portrait screen with little height: an iPhone SE, or any phone with
   *  its browser bars showing. */
  shortPortrait: '740px',
} as const;

/** The widest width that is still under a min-width breakpoint. */
const belowWidth = (width: string) => `${parseFloat(width) - 0.02}px`;

/**
 * Below a breakpoint, for the few rules a phone alone needs — where the
 * mobile-first `media` would have every wider screen undo them.
 */
export const mediaBelow = {
  sm: `@media (max-width: ${belowWidth(breakpoints.sm)})`,
  md: `@media (max-width: ${belowWidth(breakpoints.md)})`,
} as const;

/**
 * Height media queries for the landing sections. Unlike `media`, these
 * apply at the height and DOWN — a short screen is the exception a
 * section tightens for.
 */
export const heightMedia = {
  /** Short for its orientation: a laptop, a tablet on its side, a small phone. */
  short: `@media (orientation: landscape) and (max-height: ${heights.shortLandscape}), (orientation: portrait) and (max-height: ${heights.shortPortrait})`,
  /** A short phone, upright. */
  shortPhone: `@media (orientation: portrait) and (max-height: ${heights.shortPortrait})`,
  /** A short phone, upright and narrow: below `sm`. */
  smallPhone: `@media (max-width: ${belowWidth(breakpoints.sm)}) and (orientation: portrait) and (max-height: ${heights.shortPortrait})`,
} as const;
